/**
 * NOTIF-011 — what an export's notification carries and how the rows read it. One row follows
 * one export and changes in place: "… export is being prepared…" (info, silent) → "Your …
 * export is ready" (action_needed, the Download button) / "… failed" (heads-up); once the
 * download window closed the payload says `expired` and a click shows the "Export expired on …"
 * modal. Pure helpers, shared by the bell's rows, the notifications page, the toast card and the
 * store's open rule.
 */
import type { NotificationItem, NotificationTarget } from '@/services/notificationsApi'
import { metaLabel } from '@/lib/notifications/pages'

export type ExportStatus = 'queued' | 'running' | 'ready' | 'failed' | 'expired'

/** C2 — one of the export's files, numbered from 1 (`data.export.files`). */
export interface NotificationExportFile {
  part: number
  filename: string
  size_bytes: number | null
}

/** `data.export` of a `system.export` row (ExportService::payload on the API). */
export interface NotificationExport {
  uuid: string
  kind: string
  /** The page's name: "Sales History". */
  label: string
  /** The page's module: "Sales". */
  module_label: string
  status: ExportStatus
  filename: string | null
  size_bytes: number | null
  row_count: number | null
  error: string | null
  started_at: string | null
  finished_at: string | null
  expires_at: string | null
  /** C2 — the files, in order; empty until written (a row from before C2 lists none either). */
  files: NotificationExportFile[]
  /**
   * The signed download links of the parts, minted when the row was read (NotificationResource)
   * — a click uses them while they are fresh and asks `download-links` otherwise; null on a row
   * that is not ready, or from before they were handed out.
   */
  download?: NotificationExportLinks | null
}

/** One part's signed link as the row carries it: a path (the SPA puts its API's origin in front). */
export interface NotificationExportLink {
  part: number
  url: string
}

export interface NotificationExportLinks {
  /** When the links stop working (minutes after the read). */
  expires_at: string
  parts: NotificationExportLink[]
}

/** A row's links are used only with at least this much of their window left. */
export const ROW_LINKS_MIN_LEFT_MS = 60_000
/**
 * … and only while the export's own window (its `expires_at`) has at least this much left: a
 * link handed straight to the browser is the browser's from then on — the SPA never sees the
 * answer — so the server's "expired" (the prune removing the file) must not be reachable on
 * that path; an export about to expire goes through `download-links`, whose 410 is handled.
 */
export const ROW_LINKS_MIN_WINDOW_LEFT_MS = 5 * 60_000

/** How a row shows: a spinner, the Download button, a plain failure, or an expired file. */
export type ExportRowState = 'preparing' | 'ready' | 'failed' | 'expired'

const STATUSES: ExportStatus[] = ['queued', 'running', 'ready', 'failed', 'expired']

/** The export a row carries, or null for any other notification. */
export function exportOf(item: Pick<NotificationItem, 'data'>): NotificationExport | null {
  const raw = item.data?.export
  if (!raw || typeof raw !== 'object') return null
  const candidate = raw as Partial<NotificationExport>
  if (typeof candidate.uuid !== 'string' || candidate.uuid === '') return null
  const status = STATUSES.includes(candidate.status as ExportStatus)
    ? (candidate.status as ExportStatus)
    : 'queued'
  return {
    uuid: candidate.uuid,
    kind: typeof candidate.kind === 'string' ? candidate.kind : '',
    label: typeof candidate.label === 'string' ? candidate.label : 'Export',
    module_label: typeof candidate.module_label === 'string' ? candidate.module_label : 'RUKH',
    status,
    filename: typeof candidate.filename === 'string' ? candidate.filename : null,
    size_bytes: typeof candidate.size_bytes === 'number' ? candidate.size_bytes : null,
    row_count: typeof candidate.row_count === 'number' ? candidate.row_count : null,
    error: typeof candidate.error === 'string' ? candidate.error : null,
    started_at: typeof candidate.started_at === 'string' ? candidate.started_at : null,
    finished_at: typeof candidate.finished_at === 'string' ? candidate.finished_at : null,
    expires_at: typeof candidate.expires_at === 'string' ? candidate.expires_at : null,
    files: filesOf(candidate.files),
    download: linksOf(candidate.download)
  }
}

function linksOf(raw: unknown): NotificationExportLinks | null {
  if (!raw || typeof raw !== 'object') return null
  const candidate = raw as Partial<NotificationExportLinks>
  if (typeof candidate.expires_at !== 'string' || !Array.isArray(candidate.parts)) return null
  const parts: NotificationExportLink[] = []
  for (const entry of candidate.parts) {
    if (!entry || typeof entry !== 'object') continue
    const link = entry as Partial<NotificationExportLink>
    if (
      typeof link.part !== 'number' ||
      link.part <= 0 ||
      typeof link.url !== 'string' ||
      link.url === ''
    ) {
      continue
    }
    parts.push({ part: link.part, url: link.url })
  }
  return { expires_at: candidate.expires_at, parts }
}

/**
 * The row's own links for the parts asked for, when every one is there, the links have at
 * least ROW_LINKS_MIN_LEFT_MS of their window left and the export itself has at least
 * ROW_LINKS_MIN_WINDOW_LEFT_MS of its — the click then needs no request before the browser's
 * own, and nothing the server could refuse by state is left for the browser to meet (a file
 * missing on the disk still is, as it always was after a hand-over). Null otherwise (the store
 * asks `download-links`, whose refusals are handled).
 */
export function freshRowLinks(
  exp: NotificationExport,
  parts: number[],
  now: number = Date.now()
): NotificationExportLink[] | null {
  const links = exp.download
  if (!links || parts.length === 0) return null
  const expiresAt = Date.parse(links.expires_at)
  if (!Number.isFinite(expiresAt) || expiresAt - now < ROW_LINKS_MIN_LEFT_MS) return null
  const windowEnds = exp.expires_at === null ? Number.NaN : Date.parse(exp.expires_at)
  if (!Number.isFinite(windowEnds) || windowEnds - now < ROW_LINKS_MIN_WINDOW_LEFT_MS) return null
  const picked: NotificationExportLink[] = []
  for (const part of parts) {
    const link = links.parts.find((candidate) => candidate.part === part)
    if (!link) return null
    picked.push(link)
  }
  return picked
}

function filesOf(raw: unknown): NotificationExportFile[] {
  if (!Array.isArray(raw)) return []
  const files: NotificationExportFile[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const file = entry as Partial<NotificationExportFile>
    if (typeof file.filename !== 'string' || file.filename === '') continue
    files.push({
      part: typeof file.part === 'number' && file.part > 0 ? file.part : files.length + 1,
      filename: file.filename,
      size_bytes: typeof file.size_bytes === 'number' ? file.size_bytes : null
    })
  }
  return files
}

/**
 * C2 — the parts a ready export offers: its `files`, or the one file the row names when the
 * payload lists none (a row written before C2). Several parts: the Download dropdown.
 */
export function exportParts(exp: NotificationExport): NotificationExportFile[] {
  if (exp.files.length > 0) return exp.files
  return [{ part: 1, filename: exp.filename ?? 'export.csv', size_bytes: exp.size_bytes }]
}

export function isMultiPart(exp: NotificationExport): boolean {
  return exp.files.length > 1
}

/** "Part 2 · RUKHERP_…_media_002.zip · 480 MB" — a part's line in the dropdown. */
export function formatPartSize(bytes: number | null): string | null {
  if (bytes === null || !Number.isFinite(bytes) || bytes < 0) return null
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`
  return `${(bytes / 1073741824).toFixed(2)} GB`
}

/**
 * The row's state. A ready export whose window closed counts as expired here already — the
 * server's prune flips the payload later, the Download button must not wait for it.
 */
export function exportRowState(exp: NotificationExport, now: Date = new Date()): ExportRowState {
  switch (exp.status) {
    case 'queued':
    case 'running':
      return 'preparing'
    case 'failed':
      return 'failed'
    case 'expired':
      return 'expired'
    case 'ready':
      return exp.expires_at !== null && new Date(exp.expires_at).getTime() <= now.getTime()
        ? 'expired'
        : 'ready'
  }
}

/** "Oct 30, 4:15 PM" — the year only when it is not this one. */
export function formatExpiresAt(iso: string, now: Date = new Date()): string | null {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' as const }),
    hour: 'numeric',
    minute: '2-digit'
  }).format(date)
}

/** The meta line: "Sales · Sales History" (the page the export came from, not the type's module). */
export function exportMetaLabel(exp: NotificationExport): string {
  return `${exp.module_label} · ${exp.label}`
}

/**
 * The line under the title: the row's own body for ready / failed, "Started 2 min ago" while
 * preparing (the caller formats the relative time), "Expired on …" once the window closed.
 */
export function exportFactsLine(
  item: Pick<NotificationItem, 'body' | 'created_at'>,
  exp: NotificationExport,
  relative: (iso: string) => string,
  now: Date = new Date()
): string {
  switch (exportRowState(exp, now)) {
    case 'preparing':
      return `Started ${relative(item.created_at)}`
    case 'expired': {
      const when = exp.expires_at ? formatExpiresAt(exp.expires_at, now) : null
      return when ? `Expired on ${when}` : 'Expired'
    }
    default:
      return item.body ?? ''
  }
}

/**
 * Everything a row (bell, page, toast card) shows for a notification, export or not: the
 * export's state (null for any other row), the line under the title, the meta line and the
 * moment the row counts from. `relative` formats "2 min ago" (the component's formatter).
 */
export interface NotificationRowView {
  export: NotificationExport | null
  state: ExportRowState | null
  /** The row's body — "Started 2 min ago" / the facts / "Expired on …" for an export. */
  facts: string
  /** "Sales · Sales History" for an export, "Design & Development · Queue" otherwise. */
  meta: string
  /** The timestamp the row's time is formatted from. */
  at: string
}

export function rowView(
  item: NotificationItem,
  relative: (iso: string) => string,
  now: Date = new Date()
): NotificationRowView {
  const exp = exportOf(item)
  if (exp === null) {
    return {
      export: null,
      state: null,
      facts: item.body ?? '',
      meta: metaLabel(item.module, item.page_key),
      at: activityAt(item)
    }
  }
  return {
    export: exp,
    state: exportRowState(exp, now),
    facts: exportFactsLine(item, exp, relative, now),
    meta: exportMetaLabel(exp),
    at: activityAt(item)
  }
}

/** The moment a row counts from: its last state change, else its creation (API `touched_at`). */
export function activityAt(item: Pick<NotificationItem, 'created_at' | 'touched_at'>): string {
  return item.touched_at ?? item.created_at
}

/** NOTIF-011 — `data.silent`: no sound and no browser notification for this row. */
export function isSilent(item: Pick<NotificationItem, 'data'>): boolean {
  return item.data?.silent === true
}

/** NOTIF-011 — `data.toast = 'always'`: the card shows whatever the toast mode says (the answer to the person's own click). */
export function alwaysToasts(item: Pick<NotificationItem, 'data'>): boolean {
  return item.data?.toast === 'always'
}

/**
 * The server would not hand the file over: `status` is the HTTP code, `exportStatus` what it
 * said about the export (`errors.status`: queued / running / failed / expired). Lives here, not
 * in the API client, so the store can recognise it even where that module is mocked.
 */
export class ExportDownloadError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly exportStatus: string | null
  ) {
    super(message)
    this.name = 'ExportDownloadError'
  }

  /** The download window closed (410, or the server says so) — the "Export expired on …" modal. */
  get expired(): boolean {
    return this.status === 410 || this.exportStatus === 'expired'
  }
}

/**
 * The modal's target for an export that cannot be downloaded: from the row's own payload, or
 * — when the server refused the file — with the server's sentence as `reason`, which the modal
 * shows instead of the payload's date (that date may still be ahead: the file is gone for
 * another reason, owner 2026-11-01).
 */
export function expiredExportTarget(
  item: NotificationItem,
  exp: NotificationExport,
  reason: string | null = null
): NotificationTarget {
  return {
    state: 'expired',
    entity: 'export',
    label: `${exp.label} export`,
    deleted_at: exp.expires_at,
    deleted_by: null,
    reason,
    notification: item
  }
}

/**
 * How long a ready export's toast card stays after its file went to the browser ("Download
 * started" shows meanwhile), before it goes by itself.
 */
export const EXPORT_CARD_DISMISS_AFTER_START_MS = 3_000

/**
 * A download link as the API hands it back — a path (`/api/exports/…?expires=…&signature=…`) —
 * made absolute for the browser. The API does not put its own scheme and host in front: behind
 * the TLS-terminating ingress it sees plain http and would mint an `http://` link, which the
 * browser refuses to download from an https page ("redirected through an insecure connection").
 * The SPA knows where its API lives instead: `https://host/api` → `https://host` + the path;
 * `/api` (same origin) → the page's origin + the path; a prefix before `/api` is kept;
 * `//host/api` (protocol-relative) → the page's scheme and that host. A link that is already
 * absolute is left alone.
 */
export function absoluteExportUrl(url: string, apiBase: string, pageOrigin: string): string {
  if (/^https?:\/\//i.test(url)) {
    return url
  }
  const root = apiBase
    .trim()
    .replace(/\/+$/, '')
    .replace(/\/api$/, '')
  let origin = root
  if (root.startsWith('//')) {
    origin = `${/^https?:/i.exec(pageOrigin)?.[0] ?? 'https:'}${root}`
  } else if (root === '' || root.startsWith('/')) {
    origin = `${pageOrigin}${root}`
  }
  return `${origin}${url.startsWith('/') ? '' : '/'}${url}`
}

/**
 * C2 — hands a signed download link to the browser: a plain navigation to a URL the API answers
 * with `Content-Disposition: attachment`, so the browser downloads it itself (its own progress,
 * no blob through the tab's memory). Never throws; false when the browser refused the click.
 */
export function startBrowserDownload(url: string, filename: string): boolean {
  try {
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.rel = 'noopener'
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    return true
  } catch {
    return false
  }
}
